import React, { useCallback, useEffect, useState } from "react";
import { IQuestion } from "src/network/fetchApplicationQuestions";

interface Props {
  question: IQuestion;
  handleAnswerChange: (questionNumber: string, value: string) => void;
}

function MultipleSelectionQuestion({ question, handleAnswerChange }: Props) {
  const {
    question_number,
    question: questionText,
    answers,
    hasOtherAnswer,
    required,
  } = question;
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);
  const [otherAnswerText, setOtherAnswerText] = useState<string>("");
  const [hasOtherOption, setHasOtherOption] = useState<boolean>(false);

  function handleCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value, checked } = event.target;

    let updatedAnswers = [...selectedAnswers];
    if (checked) {
      updatedAnswers.push(value);
    } else {
      updatedAnswers = updatedAnswers.filter((answer) => answer !== value);
    }

    setSelectedAnswers(updatedAnswers);

    const serializedAnswers = updatedAnswers.includes("other")
      ? [
          ...updatedAnswers.filter((a) => a !== "other"),
          `Other: ${otherAnswerText}`,
        ].join(",")
      : updatedAnswers.join(",");

    handleAnswerChange(question_number, serializedAnswers);
  }

  const handleOtherTextChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setOtherAnswerText(value);
      const key = `${question_number}another_answer`;

      handleAnswerChange(key, value);
    },
    [handleAnswerChange, question_number]
  );

  const has2LevelAnswers = !!answers.find(
    (answer) => answer?.children?.length > 0
  );

  useEffect(() => {
    const isOther = answers.find((answer) =>
      answer.answer?.en?.toLowerCase().includes("other")
    );
    if (isOther) {
      setHasOtherOption(true);
    }
  }, [answers]);

  return (
    <div className={has2LevelAnswers ? "" : "multiple-selection-question"}>
      <div className="section-title">
        {questionText.en} - {questionText.ar} {required && "*"}
      </div>
      <div className="checkbox-group">
        {answers.map((answer) => {
          const isOther = answer.answer?.en?.toLowerCase() === "other";
          const isOtherSelected =
            isOther && selectedAnswers.includes(answer?.id ?? "");

          if (answer?.children?.length > 0) {
            const titleEn = answer.answer?.en ?? answer?.children[0].answer.en;
            const titleAr = answer.answer?.en
              ? answer.answer?.ar
              : answer?.children[0].answer.ar;
            return (
              <React.Fragment key={titleEn}>
                <div className="checkbox-section-title">
                  {titleEn} {titleAr} {required && "*"}
                </div>
                <div className="multiple-selection-question">
                  {answer.children.map((item) => {
                    return (
                      <label key={item?.id} className="checkbox-option">
                        <input
                          type="checkbox"
                          value={item?.id}
                          checked={selectedAnswers.includes(item?.id ?? "")}
                          onChange={handleCheckboxChange}
                        />
                        {item?.answer?.en} {item?.answer?.ar}
                        {selectedAnswers.includes(item?.id ?? "") && item?.answer?.en?.toLowerCase() === "other" && (
                <input
                  type="text"
                  className="other-answer-text"
                  value={otherAnswerText}
                  onChange={handleOtherTextChange}
                  placeholder="Please specify"
                />
              )}
                      </label>
                    );
                  })}
                </div>
              </React.Fragment>
            );
          }
          return (
            <label key={answer.id} className="checkbox-option">
              <input
                type="checkbox"
                value={answer?.id}
                checked={selectedAnswers.includes(answer?.id ?? "")}
                onChange={handleCheckboxChange}
              />
              {answer?.answer?.en} {answer?.answer?.ar}
              {isOtherSelected && (
                <input
                  type="text"
                  className="other-answer-text"
                  value={otherAnswerText}
                  onChange={handleOtherTextChange}
                  placeholder="Please specify"
                />
              )}
            </label>
          );
        })}
        {!hasOtherOption && hasOtherAnswer && (
          <label className="checkbox-option">
            <input
              type="checkbox"
              checked={selectedAnswers.includes("other")}
              onChange={handleCheckboxChange}
            />
            {"Other"} {"اخرى"}
            {selectedAnswers.includes("other") && (
              <input
                type="text"
                className="other-answer-text"
                value={otherAnswerText}
                onChange={handleOtherTextChange}
                placeholder="Please specify"
              />
            )}
          </label>
        )}
      </div>
    </div>
  );
}

export default MultipleSelectionQuestion;
